import React, { useState, useEffect } from 'react';
import { X, Shield, Save, XCircle, AlertCircle, Edit3, CheckSquare, Square, ChevronRight, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import { PerfilAcesso, Permissoes, MenuPermission } from '../types/Permission';

interface PerfilAcessoModalProps {
  perfil: PerfilAcesso | null;
  onClose: () => void;
  onSave: (perfilData: Partial<PerfilAcesso>) => Promise<void>;
}

// Estrutura de menus do sistema
const MENUS_SISTEMA = [
  { path: '/', name: 'Início', submenus: [] },
  { path: '/alunos', name: 'Alunos', submenus: [
    { path: '/alunos/novo', name: 'Novo Aluno' }
  ]},
  { path: '/professores', name: 'Professores', submenus: [] },
  { path: '/turmas', name: 'Turmas', submenus: [] },
  { path: '/niveis', name: 'Níveis', submenus: [] },
  { path: '/agenda', name: 'Agenda', submenus: [] },
  { path: '/cobrancas', name: 'Cobranças', submenus: [
    { path: '/cobrancas', name: 'Todas' },
    { path: '/cobrancas/avulsas', name: 'Avulsas' },
    { path: '/cobrancas/assinaturas', name: 'Assinaturas' },
    { path: '/cobrancas/notificacoes', name: 'Notificações' }
  ]},
  { path: '/relatorios', name: 'Relatórios', submenus: [] },
  { path: '/configuracoes', name: 'Configurações', submenus: [
    { path: '/configuracoes/usuarios', name: 'Usuários' },
    { path: '/configuracoes/perfis', name: 'Perfis de Acesso' }
  ]},
  { path: '/integracao', name: 'Integração', submenus: [
    { path: '/integracao/fireagent/configuracoes', name: 'Configurações' },
    { path: '/integracao/templates', name: 'Templates' },
    { path: '/integracao/fireagent/disparo', name: 'Disparo de Mensagens' },
    { path: '/integracao/consumo', name: 'Consumo' }
  ]}
];

// Módulos do sistema
const MODULOS_SISTEMA = [
  { id: 'alunos', name: 'Alunos' },
  { id: 'professores', name: 'Professores' },
  { id: 'turmas', name: 'Turmas' },
  { id: 'niveis', name: 'Níveis' },
  { id: 'cobrancas', name: 'Cobranças' },
  { id: 'relatorios', name: 'Relatórios' },
  { id: 'configuracoes', name: 'Configurações' },
  { id: 'integracao', name: 'Integração' }
];

const PerfilAcessoModal: React.FC<PerfilAcessoModalProps> = ({ perfil, onClose, onSave }) => {
  const { userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(!perfil);
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [cargos, setCargos] = useState<Array<{ id: string; nome: string }>>([]);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'basico' | 'menus' | 'modulos'>('basico');

  useEffect(() => {
    const loadCargos = async () => {
      try {
        const cargosData = await firebaseService.getCargos();
        setCargos(cargosData);
      } catch (error) {
        console.error('Erro ao carregar cargos:', error);
      }
    };

    loadCargos();
  }, []);

  useEffect(() => {
    if (perfil) {
      // Garantir que permissoes sempre tenha estrutura válida
      const permissoes = perfil.permissoes || {};
      const permissoesValidas = {
        menus: permissoes.menus || {},
        modulos: permissoes.modulos || {}
      };
      
      setFormData({
        nome: perfil.nome || '',
        descricao: perfil.descricao || '',
        cargoBase: perfil.cargoBase || '',
        ativo: perfil.ativo !== undefined ? perfil.ativo : true,
        permissoes: permissoesValidas
      });
      setIsEditing(false);
    } else {
      setFormData({
        nome: '',
        descricao: '',
        cargoBase: '',
        ativo: true,
        permissoes: { menus: {}, modulos: {} }
      });
      setIsEditing(true);
    }
    setErrors({});
  }, [perfil]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.cargoBase) {
      newErrors.cargoBase = 'Cargo base é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Função para normalizar chaves (remover caracteres proibidos pelo Firebase)
  const normalizeKey = (path: string): string => {
    if (!path || path.trim() === '') {
      return 'root'; // Se o path for vazio ou apenas '/', usar 'root'
    }
    
    // Remove /, [, ], #, $, . e espaços, substituindo por _
    let normalized = path.replace(/[\/\[\]#\$\.\s]/g, '_').replace(/^_+|_+$/g, '');
    
    // Se após normalizar ficar vazio, usar um valor padrão baseado no path original
    if (normalized === '' || normalized === '_') {
      normalized = path === '/' ? 'root' : 'menu_' + Math.random().toString(36).substr(2, 9);
    }
    
    return normalized;
  };

  // Função para limpar permissões removendo chaves vazias ou inválidas
  const cleanPermissoes = (permissoes: any): any => {
    if (!permissoes) {
      return { menus: {}, modulos: {} };
    }

    const cleaned: any = {
      menus: {},
      modulos: {}
    };

    // Limpar menus
    if (permissoes.menus && typeof permissoes.menus === 'object') {
      Object.keys(permissoes.menus).forEach(key => {
        // Remover chaves vazias ou inválidas
        if (key && key.trim() !== '' && !key.match(/[\/\[\]#\$\.]/)) {
          const menuData = permissoes.menus[key];
          if (menuData) {
            cleaned.menus[key] = {
              acesso: menuData.acesso || false,
              submenus: {},
              ...(menuData.path && { path: menuData.path })
            };

            // Limpar submenus
            if (menuData.submenus && typeof menuData.submenus === 'object') {
              Object.keys(menuData.submenus).forEach(subKey => {
                if (subKey && subKey.trim() !== '' && !subKey.match(/[\/\[\]#\$\.]/)) {
                  const submenuData = menuData.submenus[subKey];
                  if (submenuData) {
                    cleaned.menus[key].submenus[subKey] = {
                      acesso: submenuData.acesso || false,
                      ...(submenuData.path && { path: submenuData.path })
                    };
                  }
                }
              });
            }
          }
        }
      });
    }

    // Limpar módulos
    if (permissoes.modulos && typeof permissoes.modulos === 'object') {
      Object.keys(permissoes.modulos).forEach(key => {
        if (key && key.trim() !== '') {
          const moduloData = permissoes.modulos[key];
          if (moduloData && typeof moduloData === 'object') {
            cleaned.modulos[key] = {
              criar: moduloData.criar || false,
              editar: moduloData.editar || false,
              excluir: moduloData.excluir || false,
              visualizar: moduloData.visualizar || false
            };
          }
        }
      });
    }

    return cleaned;
  };

  const toggleMenuAccess = (menuPath: string) => {
    // Garantir que permissoes sempre tenha estrutura válida
    const currentPermissoes = {
      menus: formData.permissoes?.menus || {},
      modulos: formData.permissoes?.modulos || {}
    };

    // Usar chave normalizada para Firebase
    const normalizedKey = normalizeKey(menuPath);
    const currentAccess = currentPermissoes.menus[normalizedKey]?.acesso || false;
    currentPermissoes.menus[normalizedKey] = {
      ...currentPermissoes.menus[normalizedKey],
      acesso: !currentAccess,
      submenus: currentPermissoes.menus[normalizedKey]?.submenus || {},
      path: menuPath // Guardar o path original para referência
    };

    handleInputChange('permissoes', currentPermissoes);
  };

  const toggleSubmenuAccess = (menuPath: string, submenuPath: string) => {
    // Garantir que permissoes sempre tenha estrutura válida
    const currentPermissoes = {
      menus: formData.permissoes?.menus || {},
      modulos: formData.permissoes?.modulos || {}
    };
    
    const normalizedMenuKey = normalizeKey(menuPath);
    const normalizedSubmenuKey = normalizeKey(submenuPath);
    
    if (!currentPermissoes.menus[normalizedMenuKey]) {
      currentPermissoes.menus[normalizedMenuKey] = { 
        acesso: false, 
        submenus: {},
        path: menuPath
      };
    }
    if (!currentPermissoes.menus[normalizedMenuKey].submenus) {
      currentPermissoes.menus[normalizedMenuKey].submenus = {};
    }

    const currentAccess = currentPermissoes.menus[normalizedMenuKey].submenus[normalizedSubmenuKey]?.acesso || false;
    currentPermissoes.menus[normalizedMenuKey].submenus[normalizedSubmenuKey] = {
      ...currentPermissoes.menus[normalizedMenuKey].submenus[normalizedSubmenuKey],
      acesso: !currentAccess,
      path: submenuPath // Guardar o path original
    };

    handleInputChange('permissoes', currentPermissoes);
  };

  const toggleModuleAction = (moduloId: string, action: 'criar' | 'editar' | 'excluir' | 'visualizar') => {
    // Garantir que permissoes sempre tenha estrutura válida
    const currentPermissoes = {
      menus: formData.permissoes?.menus || {},
      modulos: formData.permissoes?.modulos || {}
    };
    
    if (!currentPermissoes.modulos[moduloId]) {
      currentPermissoes.modulos[moduloId] = { criar: false, editar: false, excluir: false, visualizar: false };
    }

    currentPermissoes.modulos[moduloId][action] = !currentPermissoes.modulos[moduloId][action];

    handleInputChange('permissoes', currentPermissoes);
  };

  const toggleAllModuleActions = (moduloId: string) => {
    // Garantir que permissoes sempre tenha estrutura válida
    const currentPermissoes = {
      menus: formData.permissoes?.menus || {},
      modulos: formData.permissoes?.modulos || {}
    };
    
    if (!currentPermissoes.modulos[moduloId]) {
      currentPermissoes.modulos[moduloId] = { criar: false, editar: false, excluir: false, visualizar: false };
    }

    const allEnabled = currentPermissoes.modulos[moduloId].criar && 
                      currentPermissoes.modulos[moduloId].editar && 
                      currentPermissoes.modulos[moduloId].excluir && 
                      currentPermissoes.modulos[moduloId].visualizar;

    currentPermissoes.modulos[moduloId] = {
      criar: !allEnabled,
      editar: !allEnabled,
      excluir: !allEnabled,
      visualizar: !allEnabled
    };

    handleInputChange('permissoes', currentPermissoes);
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      
      // Limpar permissões antes de salvar para remover chaves vazias ou inválidas
      const permissoesLimpas = cleanPermissoes(formData.permissoes);
      
      const perfilData: Partial<PerfilAcesso> = {
        ...(perfil || {}),
        nome: formData.nome,
        descricao: formData.descricao,
        cargoBase: formData.cargoBase,
        ativo: formData.ativo,
        permissoes: permissoesLimpas
      };

      console.log('💾 Salvando perfil com permissões limpas:', permissoesLimpas);
      
      await onSave(perfilData);
      setIsEditing(false);
      if (!perfil) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      setErrors({ submit: 'Erro ao salvar perfil. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (perfil) {
      // Garantir que permissoes sempre tenha estrutura válida
      const permissoes = perfil.permissoes || {};
      const permissoesValidas = {
        menus: permissoes.menus || {},
        modulos: permissoes.modulos || {}
      };
      
      setFormData({
        nome: perfil.nome || '',
        descricao: perfil.descricao || '',
        cargoBase: perfil.cargoBase || '',
        ativo: perfil.ativo !== undefined ? perfil.ativo : true,
        permissoes: permissoesValidas
      });
      setIsEditing(false);
    } else {
      onClose();
    }
    setErrors({});
  };

  const toggleMenuExpansion = (menuPath: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuPath)) {
      newExpanded.delete(menuPath);
    } else {
      newExpanded.add(menuPath);
    }
    setExpandedMenus(newExpanded);
  };

  const isCreating = !perfil;
  const displayName = perfil?.nome || 'Novo Perfil de Acesso';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {isCreating ? 'Novo Perfil de Acesso' : (isEditing ? 'Editar Perfil de Acesso' : 'Detalhes do Perfil de Acesso')}
                </h2>
                {perfil && <p className="text-sm text-gray-500 dark:text-gray-400">ID: {perfil.id}</p>}
              </div>
            </div>
            <div className="flex items-center gap-x-2">
              {!isEditing && !isCreating ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Editar
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={saving}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        {(isEditing || isCreating) && (
          <div className="border-b border-gray-200 dark:border-gray-700 px-6">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('basico')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'basico'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
                }`}
              >
                Informações Básicas
              </button>
              <button
                onClick={() => setActiveTab('menus')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'menus'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
                }`}
              >
                Menus e Submenus
              </button>
              <button
                onClick={() => setActiveTab('modulos')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'modulos'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
                }`}
              >
                Ações por Módulo
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.submit}</span>
            </div>
          )}

          {/* Tab: Informações Básicas */}
          {(activeTab === 'basico' || (!isEditing && !isCreating)) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Informações Básicas</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome do Perfil <span className="text-red-500">*</span>
                  </label>
                  {isEditing || isCreating ? (
                    <>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => handleInputChange('nome', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors.nome ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Ex: Administrador Completo, Professor Básico"
                      />
                      {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                    </>
                  ) : (
                    <p className="text-gray-900 dark:text-gray-100">{perfil?.nome || 'Não informado'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Descrição
                  </label>
                  {isEditing || isCreating ? (
                    <textarea
                      value={formData.descricao || ''}
                      onChange={(e) => handleInputChange('descricao', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Descreva o perfil de acesso..."
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-gray-100">{perfil?.descricao || 'Sem descrição'}</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Configurações</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cargo Base <span className="text-red-500">*</span>
                  </label>
                  {isEditing || isCreating ? (
                    <>
                      <select
                        value={formData.cargoBase}
                        onChange={(e) => handleInputChange('cargoBase', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors.cargoBase ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <option value="">Selecione um cargo</option>
                        {cargos.map(cargo => (
                          <option key={cargo.id} value={cargo.id}>{cargo.nome}</option>
                        ))}
                      </select>
                      {errors.cargoBase && <p className="text-red-500 text-xs mt-1">{errors.cargoBase}</p>}
                    </>
                  ) : (
                    <p className="text-gray-900 dark:text-gray-100">{perfil?.cargoBase || 'Não informado'}</p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.ativo}
                      onChange={(e) => handleInputChange('ativo', e.target.checked)}
                      disabled={!isEditing && !isCreating}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Perfil Ativo</span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Perfis inativos não podem ser atribuídos a usuários
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Menus e Submenus */}
          {activeTab === 'menus' && (isEditing || isCreating) && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Controle de Acesso a Menus</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Selecione os menus e submenus que este perfil pode acessar
              </p>

              <div className="space-y-2">
                {MENUS_SISTEMA.map((menu) => {
                  // Normalizar chave para buscar permissões
                  const normalizedMenuKey = normalizeKey(menu.path);
                  const hasAccess = (formData.permissoes?.menus && formData.permissoes.menus[normalizedMenuKey]?.acesso) || false;
                  const isExpanded = expandedMenus.has(menu.path);

                  return (
                    <div key={menu.path} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                        <div className="flex items-center gap-3">
                          {menu.submenus.length > 0 && (
                            <button
                              onClick={() => toggleMenuExpansion(menu.path)}
                              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5" />
                              ) : (
                                <ChevronRight className="h-5 w-5" />
                              )}
                            </button>
                          )}
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{menu.name}</span>
                        </div>
                        <button
                          onClick={() => toggleMenuAccess(menu.path)}
                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100"
                        >
                          {hasAccess ? (
                            <CheckSquare className="h-5 w-5 text-primary-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                          <span>{hasAccess ? 'Acesso permitido' : 'Sem acesso'}</span>
                        </button>
                      </div>

                      {isExpanded && menu.submenus.length > 0 && (
                        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                          {menu.submenus.map((submenu) => {
                            // Normalizar chave do submenu
                            const normalizedSubmenuKey = normalizeKey(submenu.path);
                            const menuPerms = formData.permissoes?.menus?.[normalizedMenuKey];
                            const submenuAccess = (menuPerms?.submenus && menuPerms.submenus[normalizedSubmenuKey]?.acesso) || false;
                            return (
                              <div key={submenu.path} className="flex items-center justify-between p-3 pl-12 hover:bg-gray-100">
                                <span className="text-sm text-gray-700 dark:text-gray-300">{submenu.name}</span>
                                <button
                                  onClick={() => toggleSubmenuAccess(menu.path, submenu.path)}
                                  className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100"
                                >
                                  {submenuAccess ? (
                                    <CheckSquare className="h-5 w-5 text-primary-600" />
                                  ) : (
                                    <Square className="h-5 w-5 text-gray-400" />
                                  )}
                                  <span>{submenuAccess ? 'Acesso permitido' : 'Sem acesso'}</span>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tab: Ações por Módulo */}
          {activeTab === 'modulos' && (isEditing || isCreating) && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Ações por Módulo</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Configure quais ações este perfil pode realizar em cada módulo do sistema
              </p>

              <div className="space-y-4">
                {MODULOS_SISTEMA.map((modulo) => {
                  const moduloPerms = (formData.permissoes?.modulos && formData.permissoes.modulos[modulo.id]) || {
                    criar: false,
                    editar: false,
                    excluir: false,
                    visualizar: false
                  };
                  const allEnabled = moduloPerms.criar && moduloPerms.editar && moduloPerms.excluir && moduloPerms.visualizar;

                  return (
                    <div key={modulo.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{modulo.name}</h4>
                        <button
                          onClick={() => toggleAllModuleActions(modulo.id)}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          {allEnabled ? 'Desmarcar todas' : 'Marcar todas'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(['criar', 'editar', 'excluir', 'visualizar'] as const).map((action) => (
                          <label
                            key={action}
                            className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={moduloPerms[action]}
                              onChange={() => toggleModuleAction(modulo.id, action)}
                              className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Visualização (quando não está editando) */}
          {!isEditing && !isCreating && perfil && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Menus Configurados</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {Object.keys(perfil.permissoes.menus || {}).length} menu(s)
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Módulos Configurados</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {Object.keys(perfil.permissoes.modulos || {}).length} módulo(s)
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerfilAcessoModal;

